import React, { useState, useEffect, useRef } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { getAuth, sendPasswordResetEmail } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { auth } from '../FirebaseConfig';

import { useLocation } from 'react-router-dom'; // Added to read the URL

// Note: Inline Firebase setup added to fix preview compilation errors. 
// Import Three.js library from CDN
// It's a great choice for dynamic, interactive backgrounds.
import * as THREE from 'https://cdnjs.cloudflare.com/ajax/libs/three.js/r128/three.module.js';

const SignIn = (props) => {
    // State to manage UI visibility and form data
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [showSuccessPopup, setShowSuccessPopup] = useState(false);
    const [showResetPassword, setShowResetPassword] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetStatus, setResetStatus] = useState({ message: '', color: '' });

    const canvasRef = useRef(null);
    const location = useLocation(); // Gets the current web address

    // --- NEW: Capture Referral Code ---
    useEffect(() => {
        // Look at the web address
        const params = new URLSearchParams(location.search);
        // Find the "ref=" part
        const refCode = params.get("ref");
        
        if (refCode) {
            // Save it to the browser's memory so it doesn't get lost during sign up
            localStorage.setItem("ucart_referral_code", refCode);
            console.log("Referral code saved to memory:", refCode);
        }
    }, [location]);

    // useEffect hook to set up the 3D background animation
    useEffect(() => {
        if (!canvasRef.current) return;

        // --- Three.js Setup ---
        // Create the scene, camera, and renderer
        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 0.1, 1000);
        const renderer = new THREE.WebGLRenderer({ canvas: canvasRef.current, alpha: true });
        renderer.setSize(window.innerWidth, window.innerHeight);

        // --- Create Star Particles ---
        const starGeometry = new THREE.BufferGeometry();
        const starMaterial = new THREE.PointsMaterial({
            color: 0x888888, // A subtle white/gray color
            size: 1.5,
            sizeAttenuation: true, // Stars appear smaller with distance
            transparent: true,
            opacity: 0.8
        });
        const starVertices = [];
        for (let i = 0; i < 1000; i++) {
            // Generate random positions for each star particle
            const x = (Math.random() - 0.5) * 2000;
            const y = (Math.random() - 0.5) * 2000;
            const z = (Math.random() - 0.5) * 2000;
            starVertices.push(x, y, z);
        }
        starGeometry.setAttribute('position', new THREE.Float32BufferAttribute(starVertices, 3));
        const stars = new THREE.Points(starGeometry, starMaterial);
        scene.add(stars);

        // Position the camera
        camera.position.z = 500;

        // --- Animation Loop ---
        const animate = () => {
            requestAnimationFrame(animate);
            // Rotate the star field to create a sense of movement
            stars.rotation.x += 0.0005;
            stars.rotation.y += 0.0005;
            renderer.render(scene, camera);
        };

        // Handle window resizing for responsiveness
        const onWindowResize = () => {
            camera.aspect = window.innerWidth / window.innerHeight;
            camera.updateProjectionMatrix();
            renderer.setSize(window.innerWidth, window.innerHeight);
        };
        window.addEventListener('resize', onWindowResize);

        // Start the animation loop
        animate();

        // --- Cleanup function on component unmount ---
        return () => {
            window.removeEventListener('resize', onWindowResize);
            renderer.dispose();
            // Clear the scene to free up resources
            while(scene.children.length > 0){
                scene.remove(scene.children[0]);
            }
        };
    }, []); 

    // --- Form Submission Logic ---
    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await props.handleAuth();
            if (props.isSignUp) {
                setShowSuccessPopup(true);
            }
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    // --- Password Reset Logic ---
    const handlePasswordReset = async () => {
        if (!resetEmail) {
            setResetStatus({ message: 'Please enter your email.', color: 'text-red-500' });
            return;
        }

        try {
            await sendPasswordResetEmail(auth, resetEmail);
            setResetStatus({
                message: '✅ Reset link sent! Please check your email.',
                color: 'text-green-400',
            });
            setTimeout(() => {
                setShowResetPassword(false);
                setResetEmail('');
                setResetStatus({ message: '', color: '' });
            }, 3000);
        } catch (err) {
            console.error("Reset failed:", err.code, err.message);
            setResetStatus({
                message:
                    err.code === 'auth/user-not-found'
                        ? 'No account found with this email.'
                        : 'Something went wrong. Try again.',
                color: 'text-red-500',
            });
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center font-['Space_Grotesk'] text-white relative">
            {/* The canvas for the 3D animation. It's positioned to cover the whole background. */}
            <canvas ref={canvasRef} className="fixed top-0 left-0 w-full h-full z-0"></canvas>

            {/* The form container, positioned on top of the canvas with a higher z-index */}
            <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="bg-white/10 backdrop-blur-lg border border-white/20 p-8 rounded-3xl shadow-2xl w-full max-w-sm space-y-6 z-10"
            >
                <h2 className="text-3xl font-bold text-center text-green-400">
                    {props.isSignUp ? 'Create Account' : 'Sign In'}
                </h2>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <input
                        type="email"
                        placeholder="Email"
                        value={props.email}
                        onChange={(e) => props.setEmail(e.target.value)}
                        className="w-full p-3 bg-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 transition-all placeholder-gray-300"
                        required
                    />

                    <div className="relative">
                        <input
                            type={showPassword ? 'text' : 'password'}
                            placeholder="Password"
                            value={props.password}
                            onChange={(e) => props.setPassword(e.target.value)}
                            className="w-full p-3 bg-white/10 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-400 transition-all placeholder-gray-300"
                            required
                        />
                        <span
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-3 top-1/2 -translate-y-1/2 cursor-pointer text-gray-400 hover:text-green-400 transition-colors"
                        >
                            {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </span>
                    </div>

                    {!props.isSigned && (
                        <p
                            className="text-sm text-right text-orange-300 cursor-pointer hover:underline transition-colors"
                            onClick={() => setShowResetPassword(true)}
                        >
                            Forgot password?
                        </p>
                    )}

                    <motion.button
                        type="submit"
                        className="w-full py-3 bg-green-600 rounded-xl font-bold hover:bg-green-500 transition-colors shadow-lg"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        disabled={loading}
                    >
                        {loading ? (
                            <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin mx-auto"></div>
                        ) : (
                            props.isSignUp ? 'Sign Up' : 'Sign In'
                        )}
                    </motion.button>
                </form>

                <p className="text-center text-sm mt-4">
                    {props.isSignUp ? 'Already have an account?' : "Don't have an account?"}
                    <span
                        onClick={() => props.setIsSignUp(!props.isSignUp)}
                        className="text-orange-400 cursor-pointer ml-2 hover:underline font-semibold transition-colors"
                    >
                        {props.isSignUp ? 'Sign In' : 'Sign Up'}
                    </span>
                </p>
            </motion.div>

            {/* Loading Spinner Popup */}
            <AnimatePresence>
                {loading && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="w-16 h-16 border-4 border-t-transparent border-green-400 rounded-full animate-spin"
                            initial={{ scale: 0.5 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.5 }}
                            transition={{ duration: 0.4 }}
                        />
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Success Popup for SignUp */}
            <AnimatePresence>
                {showSuccessPopup && (
                    <motion.div
                        className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-green-700 p-6 rounded-xl text-center shadow-xl"
                            initial={{ y: -100, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 100, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 200 }}
                        >
                            <h2 className="text-xl font-bold mb-2">🎉 Account Created!</h2>
                            <p className="text-white">Welcome aboard</p>
                            <button
                                onClick={() => setShowSuccessPopup(false)}
                                className="mt-4 px-4 py-2 bg-white text-green-700 font-medium rounded hover:bg-gray-200"
                            >
                                Close
                            </button>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Forgot Password Modal */}
            <AnimatePresence>
                {showResetPassword && (
                    <motion.div
                        className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                    >
                        <motion.div
                            className="bg-white/10 border border-white/20 p-6 rounded-lg w-full max-w-sm shadow-lg"
                            initial={{ scale: 0.9 }}
                            animate={{ scale: 1 }}
                            exit={{ scale: 0.8 }}
                        >
                            <h2 className="text-xl font-semibold text-white mb-2 text-center">Reset Your Password</h2>
                            <p className="text-sm text-white text-center mb-4">
                                No worries — enter your email and we’ll send you a reset link. Check your inbox.
                            </p>
                            <input
                                type="email"
                                placeholder="Enter your email"
                                value={resetEmail}
                                onChange={(e) => setResetEmail(e.target.value)}
                                className="w-full mb-3 px-4 py-2 rounded bg-white/10 border border-white/20 text-white placeholder-gray-300"
                            />
                            {resetStatus.message && (
                                <p className={`text-sm mb-2 text-center ${resetStatus.color}`}>{resetStatus.message}</p>
                            )}
                            <div className="flex gap-3 justify-end mt-2">
                                <button
                                    onClick={() => {
                                        setShowResetPassword(false);
                                        setResetStatus({ message: '', color: '' });
                                        setResetEmail('');
                                    }}
                                    className="text-white px-4 py-2 rounded hover:bg-white/20 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePasswordReset}
                                    disabled={!resetEmail}
                                    className={`px-4 py-2 rounded text-white font-semibold transition-colors ${
                                        !resetEmail ? 'bg-gray-500 cursor-not-allowed' : 'bg-green-600 hover:bg-green-500'
                                    }`}
                                >
                                    Send Reset Link
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
};

export default SignIn;